import React, { useCallback, useEffect, useState } from "react"
import { Box, Progress } from "@carrotfertility/carotene-core"
import { BodyWithSidebarLayout } from "../../features/global-ui/page-layout"
import { TalkToCarrotExpertsSidebar } from "./TalkToCarrotExpertsSidebar"
import { mapContentToExpertBio } from "#/components/read/shared/ExpertBioMapper"
import { useGetSpecialistChatMetadata } from "./hooks/useGetSpecialistChatMetadata"
import { TalkToCarrotSchedulingError } from "./TalkToCarrotSchedulingError"
// TypeCalendlyLink is the TimeTap link type, auto-generated from Contentful
import { TypeCalendlyLink } from "#/types/contentful"
import {
  TimeTapScheduler,
  TimeTapSpecialistConfirmation,
  useReportToHeapTimeTapChatScheduled
} from "#/components/time-tap/TimeTapScheduler"
import { useResolvedTimeTapLink } from "#/components/talk-to-carrot/hooks/useResolvedTimeTapLink"
// Experiment is currently stopped but keeping the tracking in case it's reused
import { useTrackExperimentEvent } from "../views/register/hooks/useExperiments"

export const TalkToCarrotScheduleSpecialistChat = ({
  timeTapLink
}: {
  timeTapLink: TypeCalendlyLink<"WITHOUT_UNRESOLVABLE_LINKS", string>
}): React.ReactElement => {
  const { country, specialistType, language } = useGetSpecialistChatMetadata()
  const { trackExperimentEvent } = useTrackExperimentEvent()
  const [showChatConfirmation, setShowChatConfirmation] = useState(false)

  const resolvedTimeTapLink = useResolvedTimeTapLink(timeTapLink)
  const id = resolvedTimeTapLink?.data?.sys?.id
  const { expertBios, reasonId, locationId, timeTapEnvironment } = resolvedTimeTapLink?.data?.fields || {}

  useReportToHeapTimeTapChatScheduled(
    setShowChatConfirmation,
    {
      eventName: "Care Team specialist chat scheduled",
      properties: {
        "Chat id": id,
        "Specialist chat country": country,
        "Specialist chat type": specialistType,
        "Specialist chat language": language
      }
    },
    Boolean(id)
  )

  // Experiment is currently stopped but keeping the tracking in case it's reused
  const handleChatScheduled = useCallback(
    (event: MessageEvent) => {
      if (event?.data === "TimeTapChatScheduled") {
        trackExperimentEvent("talk-to-carrot-expert-chat-scheduled")
      }
    },
    [trackExperimentEvent]
  )

  useEffect(() => {
    window.addEventListener("message", handleChatScheduled)
    return () => window.removeEventListener("message", handleChatScheduled)
  }, [handleChatScheduled])

  const expertsSidebar =
    expertBios?.length > 0 ? <TalkToCarrotExpertsSidebar expertBios={expertBios.map(mapContentToExpertBio)} /> : null

  const TimeTapSpecialistsScheduler = (
    <Box sx={{ blockSize: "52rem" }}>
      {showChatConfirmation ? (
        <TimeTapSpecialistConfirmation />
      ) : (
        <TimeTapScheduler reasonId={reasonId} locationId={locationId} timeTapEnvironment={timeTapEnvironment} />
      )}
    </Box>
  )

  return resolvedTimeTapLink.isLoading ? (
    <Progress />
  ) : resolvedTimeTapLink.isError ? (
    <TalkToCarrotSchedulingError />
  ) : (
    <BodyWithSidebarLayout sidebar={expertsSidebar}>{TimeTapSpecialistsScheduler}</BodyWithSidebarLayout>
  )
}
